import PropTypes from "prop-types"
import React from "react"
import { useForm } from "react-hook-form"
import { useMutation } from "react-query"
import Login from "src/main/User/Login"
import Signup from "src/main/User/Signup"

import Button from "src/components/Button"
import Form from "src/components/Form"
import Modal from "src/components/Modal"

import { connectWithMarina } from "src/api/MarinaInvite"

import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"

const MarinaInviteModal = ({
  isOpen,
  setIsOpen,
  variant,
  marina,
  minimumPasswordLength,
  setShowSuccessBanner,
  setVariant,
  trackingProps,
}) => {
  const handleClose = () => {
    setIsOpen(false)
  }
  const tracker = useTracker()

  const showToast = useToast()
  const { register, watch } = useForm({
    defaultValues: {
      tos: false,
    },
  })

  const { mutate: connectWithMarinaMutation, isLoading } = useMutation(
    () => connectWithMarina({ marinaId: marina.id }),
    {
      onSuccess: (data) => {
        tracker.trackEvent("marina_invite:connected", trackingProps)
        setShowSuccessBanner(true)
        handleClose()
        showToast(data.message, {
          type: "success",
        })
      },
      onError: (error) => {
        setShowSuccessBanner(false)
        showToast(error.message, {
          type: "error",
        })
      },
    }
  )

  const renderBody = () => {
    if (variant === "loggedIn") {
      return (
        <div className="flex flex-col px-2 pb-2">
          <p className="mb-3">
            You’re confirming that you’d like to connect your Dockwa account
            with {marina.name}. This links your personal Dockwa account to your
            profile at the marina, keeping your records with the marina up to
            date when you have active bookings.
          </p>
          <Form.Checkbox
            {...register("tos", {
              required:
                "You must agree to the terms of service in order to connect",
            })}
          >
            <span>
              I agree to{" "}
              {marina.terms && (
                <span>
                  {marina.name}&apos;s
                  <a href={`/home/terms_and_conditions/${marina.mcomId}`}>
                    Terms of Service
                  </a>{" "}
                  and{" "}
                </span>
              )}
              Dockwa&apos;s{" "}
              <a href="https://ahoy.dockwa.com/about-us/terms-of-service">
                Terms of Service
              </a>
              &nbsp;&&nbsp;
              <a href="https://ahoy.dockwa.com/privacy">Privacy Policy</a>
            </span>
          </Form.Checkbox>
          <div className="mt-6 flex justify-end">
            <Button
              variant="primary"
              isLoading={isLoading}
              onClick={connectWithMarinaMutation}
              disabled={!watch("tos")}
            >
              {isLoading ? "Connecting" : `Connect with ${marina.name}`}
            </Button>
          </div>
        </div>
      )
    } else if (variant === "login") {
      return (
        <div className="flex flex-col space-y-2">
          <span>
            New to Dockwa?{" "}
            <Button
              variant="link"
              onClick={() => {
                setVariant("signup")
              }}
            >
              Sign up
            </Button>
          </span>
          <Login handleClose={handleClose} />
        </div>
      )
    } else {
      return (
        <div className="flex flex-col space-y-2">
          <span>
            Already have an account?{" "}
            <Button
              variant="link"
              onClick={() => {
                setVariant("login")
              }}
            >
              Log in
            </Button>
          </span>
          <Signup
            minimumPasswordLength={minimumPasswordLength}
            handleClose={handleClose}
          />
        </div>
      )
    }
  }

  const renderTitle = () => {
    if (variant === "loggedIn") {
      return `Connect with ${marina.name}`
    } else if (variant === "login") {
      return "Log in"
    } else {
      return "Sign up"
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <Modal.Header title={renderTitle()} />
      <Modal.Body>
        <div className="flex flex-col p-2">{renderBody()}</div>
      </Modal.Body>
    </Modal>
  )
}

MarinaInviteModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(["loggedIn", "login", "signup"]),
  marina: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    terms: PropTypes.string,
    mcomId: PropTypes.string,
  }).isRequired,
  minimumPasswordLength: PropTypes.number.isRequired,
  setShowSuccessBanner: PropTypes.func.isRequired,
  setVariant: PropTypes.func.isRequired,
  trackingProps: PropTypes.object.isRequired,
}

export default MarinaInviteModal
